import React, { useEffect, useMemo, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { Box, Slide, Typography, useScrollTrigger } from "@material-ui/core";
import {
  Icon,
  IconName,
  B2BButton,
  useDeviceTypes,
  TravelWalletDetailsModal,
  getTravelOfferCardContent,
  ITravelOfferCardContent,
  ITravelWalletDetailsModalProps,
  CurrencyFormatters,
  TravelCreditHistoryModalContent,
  GenericDetailsCard,
  GenericDetailsCardTopSectionComponentEnum,
  GenericDetailsCardComponentEnum,
  ActionLink,
  CloseButtonIcon,
  isOfferHSPTiered,
  getWalletBannerCreditToShow,
  isSpendAndGetOffer,
  SanitizedHtml,
} from "halifax";
import {
  LandingPageTakeovers,
  TakeoverEnum,
  TravelWalletTakeover,
  StatusTakeover,
  Transaction,
  TravelWalletCredit,
  RewardsAccount,
  DISCOUNT_PROMO_TERMS_CONDITIONS,
  REFUNDABLE_FARES_POINT_ONE,
  REFUNDABLE_FARES_TITLE,
  REFUNDABLE_FARES_LOCATION,
  REFUND_COVERAGE_PERCENTAGE_HARDCODE_80,
  REFUNDABLE_FARES_POINT_THREE,
  TERMS_COPY,
  CreditDetail,
  TravelWalletOffer,
  VIEWED_TRAVEL_OFFER,
  ENGAGED_CREDIT_CTA,
  VIEW_CREDIT_HISTORY,
  ENGAGED_OFFER_CTA,
  VIEWED_OFFER_BANNER,
  CorpSessionInfo,
  TAKEOVER_NOTICE_VIEWED,
} from "redmond";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  CDN_ASSETS,
  getPortalName,
  isCorpTenant,
  useUserContext,
} from "@capone/common";
import { useExperimentsById } from "@capone/experiments";
import { fetchLandingPageTakeovers as landingPageTakeovers } from "../../api/v1/takeovers/fetchLandingPageTakeovers";
import {
  ADDITIONAL_MOBILE_REDESIGN_HEADER_FOOTER_PATHS,
  PATH_TRAVEL_WALLET,
  PATH_TERMS_CFAR,
  PATH_TRAVEL_SALE,
} from "../../utils/urlPaths";
import config from "../../utils/config";
import * as constants from "./constants";
import "./styles.scss";
import {
  ANNUAL_TRAVEL_CREDITS,
  AVAILABLE,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_WALLET_CREDIT_EXPERIMENT,
  CFAR_DISCOUNT,
  CFAR_DISCOUNT_CONTROL,
  CFAR_DISCOUNT_VARIANTS,
  useExperiments,
  CFAR_DISCOUNT_10_PERCENT,
  CFAR_DISCOUNT_25_PERCENT,
  CFAR_DISCOUNT_75_PERCENT,
  CFAR_DISCOUNT_100_PERCENT,
  CfarDiscountVariantType,
  TRAVEL_SALE,
  TRAVEL_SALE_VARIANTS,
  CONTROL,
  TRAVEL_SALE_ACTIVE,
  CORP_VXB_MIGRATION_TAKEOVER_FORCE_SHOW,
  CORP_VXB_MIGRATION_TAKEOVER,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { fetchTravelWalletCreditHistory } from "../../api/v1/takeovers/fetchTravelWalletCreditHistory";
import {
  CORPORATE_VXB_MIGRATION_TAKEOVER_CTA,
  CORPORATE_VXB_MIGRATION_TAKEOVER_LABEL,
  CORPORATE_VXB_MIGRATION_TAKEOVER_STORAGE_KEY,
  VIEW_CREDIT_HISTORY_CTA,
  VIEW_CREDIT_HISTORY_DESCRIPTION,
} from "./constants";
import { trackEvent } from "../../api/v1/trackEvent";
import { trackMediaEvent } from "../../api/v1/trackHTSMedia";
import { MediaEventType, MediaPlacement } from "redmond";

interface IHomepageTakeoverProps extends RouteComponentProps {
  rewardsAccounts?: RewardsAccount[];
}

interface IHomepageTakeoverContent {
  title?: string | JSX.Element;
  subtitle: string | JSX.Element;
  ctaCopy?: string;
  ctaAction?: () => void;
  showTravelOfferModal?: boolean;
  showIndividualTravelCreditModal?: boolean;
  modalVariant?: ITravelWalletDetailsModalProps["variant"];
  modalHeaderText?: string;
  travelOfferModalContent?: ITravelOfferCardContent;
  creditDetailsCardProps?: ITravelWalletDetailsModalProps["creditDetailsCardProps"];
  className?: string;
  iconName?: string;
  showDismiss?: boolean;
  onDismissAction?: () => void;
  showDismissMobile?: boolean;
}

const HideOnScroll = ({
  children,
  disable,
}: {
  children: JSX.Element;
  disable: boolean;
}) => {
  const trigger = useScrollTrigger({ disableHysteresis: true });

  return (
    <Slide appear={false} direction="down" in={disable ? true : !trigger}>
      {children}
    </Slide>
  );
};

interface IDiscountPromoText {
  homescreenBanner: string;
  modalBanner: string;
  modalBody: string;
}

export const HomepageTakeover = (props: IHomepageTakeoverProps) => {
  const { history, rewardsAccounts } = props;
  const { matchesMobile } = useDeviceTypes();
  const [content, setContent] = useState<IHomepageTakeoverContent>();
  const [openRFModal, setOpenRFModal] = useState<boolean>(false);
  const [openTravelOfferModal, setOpenTravelOfferModal] = React.useState(false);
  const expState = useExperiments();
  const [creditsHistoryTransactions, setCreditsHistoryTransaction] = useState<
    Transaction[]
  >([]);
  const [creditHistoryModalContent, setCreditsHistoryModalContent] =
    useState<TravelCreditHistoryModalContent>();
  const [takeoverCredit, setTakeoverCredit] = React.useState<
    TravelWalletCredit | undefined
  >();
  const [hideTakeover, setHideTakeover] = React.useState(false);
  const [hasSetContent, setHasSetContent] = useState(false);
  const [creditBreakdown, setCreditBreakdown] = React.useState<CreditDetail[]>(
    []
  );

  const [discountPromoText, setDiscountPromoText] =
    React.useState<IDiscountPromoText>({} as IDiscountPromoText);
  const [takeoverOffer, setTakeoverOffer] = React.useState<
    TravelWalletOffer | undefined
  >(undefined);
  const isTravelWalletCreditsExperiment =
    getExperimentVariant(
      expState.experiments,
      TRAVEL_WALLET_CREDIT_EXPERIMENT
    ) === AVAILABLE;

  const isCreditAndOfferStackingExperimentV1 =
    getExperimentVariant(expState.experiments, CREDIT_OFFER_STACKING_V1) ===
    AVAILABLE;

  const isTravelCreditHistoryExperiment =
    getExperimentVariant(
      expState.experiments,
      TRAVEL_CREDIT_HISTORY_EXPERIMENT
    ) === AVAILABLE;

  const cfarDiscountVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    CFAR_DISCOUNT,
    CFAR_DISCOUNT_VARIANTS
  );
  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const vxbMigrationForceShowFlag = getExperimentVariant(
    expState.experiments,
    CORP_VXB_MIGRATION_TAKEOVER_FORCE_SHOW
  );
  const vxbMigrationTakeoverFlag = getExperimentVariant(
    expState.experiments,
    CORP_VXB_MIGRATION_TAKEOVER
  );

  const travelSalesEventVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS
  );

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const hasVentureXBusinessAccountCount =
    rewardsAccounts?.filter((account) =>
      account.productDisplayName.includes("Venture X Business")
    ) ?? [];

  const hasDismissedCorpSparkMigrationBanner =
    localStorage.getItem(CORPORATE_VXB_MIGRATION_TAKEOVER_STORAGE_KEY) ===
    "true";

  const { sessionInfo } = useUserContext(config.TENANT);
  const isCorporate = isCorpTenant(config.TENANT);
  const isNonFinancialUser =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "NFU";
  const { CFAR_DISCOUNT_EXPIRATION_DATE_MILLIS } = config;
  const EXP_DATE = CFAR_DISCOUNT_EXPIRATION_DATE_MILLIS
    ? dayjs(parseInt(CFAR_DISCOUNT_EXPIRATION_DATE_MILLIS)).format(
        "MMMM D, YYYY"
      )
    : "";
  const getDiscountPromoText = (
    experiment: CfarDiscountVariantType
  ): IDiscountPromoText => {
    switch (experiment) {
      case CFAR_DISCOUNT_CONTROL:
        return { homescreenBanner: "", modalBanner: "", modalBody: "" };
      case CFAR_DISCOUNT_10_PERCENT:
        return {
          homescreenBanner: "10% off",
          modalBanner: "10% off limited-time promotion",
          modalBody: "10% off",
        };
      case CFAR_DISCOUNT_25_PERCENT:
        return {
          homescreenBanner: "25% off",
          modalBanner: "25% off limited-time promotion",
          modalBody: "25% off",
        };
      case CFAR_DISCOUNT_75_PERCENT:
        return {
          homescreenBanner: "75% off",
          modalBanner: "75% off limited-time promotion",
          modalBody: "75% off",
        };
      case CFAR_DISCOUNT_100_PERCENT:
        return {
          homescreenBanner: "Free limited-time promotion",
          modalBanner: "Free limited-time promotion",
          modalBody: "no additional fee",
        };
    }
  };

  const trackOfferEngagementEvent = (offer?: TravelWalletOffer) => {
    trackEvent({
      eventName: ENGAGED_OFFER_CTA,
      properties: {
        location: "homepage",
        entry_type: "banner",
        offer_name: offer ? undefined : "multiple",
        funnel: offer?.funnels.join(","),
      },
      encryptedProperties: offer
        ? [offer.trackingPropertiesV2?.encryptedProperties ?? ""]
        : [],
    });

    offer?.trackingPropertiesV2 &&
      trackMediaEvent({
        eventType: MediaEventType.Click,
        trackingProperties: offer?.trackingPropertiesV2,
        placement: MediaPlacement.Homepage,
      });
  };

  const trackViewedOfferBannerEvent = (offer?: TravelWalletOffer) => {
    trackEvent({
      eventName: VIEWED_OFFER_BANNER,
      properties: {
        location: "homepage",
        offer_name: offer ? undefined : "multiple",
      },
      encryptedProperties: offer
        ? [offer.trackingPropertiesV2?.encryptedProperties ?? ""]
        : [],
    });
  };

  const discountContent = React.useMemo(
    () => (
      <div>
        Plan your trip worry-free. After selecting your flight, add the
        flexibility to cancel it for any reason for{" "}
        {cfarDiscountVariant === CFAR_DISCOUNT_100_PERCENT ? (
          <span className="cross-out">a fee</span>
        ) : null}{" "}
        <span className="green-text">{discountPromoText.modalBody}</span>{" "}
        {cfarDiscountVariant !== CFAR_DISCOUNT_100_PERCENT
          ? "the usual fee "
          : ""}
        (available for 1 booking per account on eligible flights
        {EXP_DATE && <> departing before {EXP_DATE}</>}).
      </div>
    ),
    [cfarDiscountVariant, discountPromoText]
  );

  const maintenanceExperiment = useExperimentsById("cap1-portal-maintenance");
  const isMaintenaceBannerActive = useMemo(
    () =>
      Boolean(maintenanceExperiment) &&
      maintenanceExperiment?.variant === "banner",
    [maintenanceExperiment]
  );

  useEffect(() => {
    if (
      expState.experiments.length === 0 ||
      !rewardsAccounts?.length ||
      hasSetContent ||
      maintenanceExperiment === undefined // to avoid running into the else when maintenance page banner exp isn't set yet
    ) {
      return;
    }

    const shouldShowVxbMigration = (() => {
      if (vxbMigrationForceShowFlag === "force-hide") {
        return false;
      }
      if (vxbMigrationForceShowFlag === "force-show") {
        return true;
      }
      if (vxbMigrationTakeoverFlag === "available") {
        return (
          !hasDismissedCorpSparkMigrationBanner &&
          hasVentureXBusinessAccountCount.length > 0
        );
      }
      return false;
    })();

    if (shouldShowVxbMigration) {
      setHasSetContent(true);
      trackEvent({
        eventName: TAKEOVER_NOTICE_VIEWED,
        properties: {
          takeover_type: "vxb_migration_takeover_banner",
          num_venture_x_business_products_in_COT:
            hasVentureXBusinessAccountCount.length,
          force_show_flag: vxbMigrationForceShowFlag,
        },
      });
      setContent({
        subtitle: CORPORATE_VXB_MIGRATION_TAKEOVER_LABEL,
        ctaCopy: CORPORATE_VXB_MIGRATION_TAKEOVER_CTA,
        iconName: "lightbulb-outline",
        className: "corp-vxb-migration-takeover",
        ctaAction: () => {
          window.open(
            "https://businesstravel.capitalone.com/faq/",
            "_blank",
            "noopener,noreferrer"
          );
        },
        showDismiss: true,
        onDismissAction: () => {
          localStorage.setItem(
            CORPORATE_VXB_MIGRATION_TAKEOVER_STORAGE_KEY,
            "true"
          );
        },
        showDismissMobile: true,
      });
      return;
    }

    if (isMaintenaceBannerActive) {
      setContent({
        title: maintenanceExperiment?.data.title,
        subtitle: maintenanceExperiment?.data.subtext,
      });
    } else {
      fetchLandingPageTakeovers().then((response) => {
        if (
          expState !== undefined ||
          (response as LandingPageTakeovers).takeovers !== undefined ||
          (response as LandingPageTakeovers).walletTakeover !== undefined
        ) {
          const { takeovers } = response as LandingPageTakeovers;
          const walletTakeover = (response as LandingPageTakeovers)
            .walletTakeover as TravelWalletTakeover;

          const statusTakeover = (
            (response as LandingPageTakeovers).statusTakeover as StatusTakeover
          )?.message
            ? (response as LandingPageTakeovers).statusTakeover
            : undefined;
          const pfTakeover = takeovers?.length > 0 ? takeovers[0] : undefined;
          const walletTakeoverWithOffer =
            walletTakeover?.offerCount > 0 || !!walletTakeover?.credit
              ? walletTakeover
              : undefined;

          // note: there could be more types of takeovers other than just PF; when more of them are added,
          // this logic needs to be revisited
          const takeover =
            statusTakeover || pfTakeover || walletTakeoverWithOffer;

          // use the cfar discount related banner
          if (
            cfarDiscountVariant !== CFAR_DISCOUNT_CONTROL &&
            takeover?.Takeover
          ) {
            const discountPromoText: IDiscountPromoText =
              getDiscountPromoText(cfarDiscountVariant);

            setContent({
              subtitle: `<strong>${discountPromoText.homescreenBanner} | Add the flexibility to cancel your flight for any reason </strong> (on eligibile flights).`,
              className: "cfar-discount",
              iconName: "check-shield-blue",
              ctaCopy: "View Details",
              ctaAction: () => setOpenRFModal(true),
            });
            setDiscountPromoText(discountPromoText);
            return;
          }

          const individualCreditDetailToShow =
            takeover && "creditBreakdown" in takeover
              ? getWalletBannerCreditToShow(
                  takeover.creditBreakdown,
                  rewardsAccounts
                )
              : undefined;

          switch (takeover?.Takeover) {
            case TakeoverEnum.StatusTakeover:
              setContent({ subtitle: takeover.message });
              break;
            case TakeoverEnum.TravelWalletTakeover:
              if (travelSalesEventVariant === TRAVEL_SALE_ACTIVE) {
                setContent({
                  subtitle: (
                    <>
                      Our first-ever travel sale has landed.{" "}
                      <Link
                        to={`${PATH_TRAVEL_SALE}?entryType=search_banner`}
                        target={matchesMobile ? undefined : "_blank"}
                        className="travel-sale-banner-link"
                      >
                        Explore destinations on sale
                      </Link>
                    </>
                  ),
                  iconName: "offer-tag",
                  className: "travel-sale-takeover",
                  showDismiss: true,
                });
              } else if (
                // For COBT, we won't favor credits if there is an offer.
                (takeover.credit || individualCreditDetailToShow) &&
                (isCorporate
                  ? !takeover.bestOffer
                  : individualCreditDetailToShow ||
                    Math.abs(takeover.bestOffer?.amount.amount || 0) <
                      Math.abs(takeover.credit?.amount.amount || 0))
              ) {
                if (individualCreditDetailToShow) {
                  setContent({
                    subtitle: constants.INDIVIDUAL_CREDIT_SUBTITLE(
                      individualCreditDetailToShow
                    ),
                    showTravelOfferModal: true,
                    iconName: "piggy-bank-icon",
                    className: takeover.Takeover,
                    modalVariant: "credit",
                    showDismiss: true,
                    modalHeaderText: constants.CREDITS_MODAL_HEADER_TEXT(
                      sessionInfo?.userInfo.firstName
                    ),
                    creditDetailsCardProps:
                      constants.CREDITS_MODAL_DETAILS_CARD_PROPS(
                        creditBreakdown,
                        sessionInfo?.userInfo.firstName,
                        takeover.credit?.amount,
                        isCreditAndOfferStackingExperimentV1,
                        isNonFinancialUser
                      ),
                  });
                } else {
                  setContent({
                    subtitle: constants.CREDITS_SUBTITLE(
                      takeover.credit?.amount
                    ),
                    showTravelOfferModal: true,
                    iconName: "piggy-bank-icon",
                    className: takeover.Takeover,
                    modalVariant: "credit",
                    showDismiss: true,
                    modalHeaderText: constants.CREDITS_MODAL_HEADER_TEXT(
                      sessionInfo?.userInfo.firstName
                    ),
                    creditDetailsCardProps:
                      constants.CREDITS_MODAL_DETAILS_CARD_PROPS(
                        creditBreakdown,
                        sessionInfo?.userInfo.firstName,
                        takeover.credit?.amount,
                        isCreditAndOfferStackingExperimentV1,
                        isNonFinancialUser
                      ),
                  });
                }

                if (
                  isTravelCreditHistoryExperiment &&
                  !!creditsHistoryTransactions.length
                ) {
                  setCreditsHistoryModalContent({
                    header: constants.CREDIT_BALANCE_DESCRIPTION(
                      sessionInfo?.userInfo.firstName
                    ),
                    balanceText: constants.CREDIT_HISTORY_BALANCE_TEXT(
                      CurrencyFormatters.getSymbol(
                        takeover.credit?.amount.currency
                      ),
                      CurrencyFormatters.get(
                        takeover.credit?.amount.currency
                      ).format(
                        (takeover.credit?.amount.amount &&
                          takeover.credit?.amount.amount * -1) ||
                          0
                      )
                    ),
                    subHeader: constants.CREDIT_HISTORY_MODAL_SUBHEADER,
                    creditListHeader:
                      constants.CREDIT_HISTORY_MODAL_LIST_HEADER,
                  });
                }

                setTakeoverCredit(takeover.credit);
                setCreditBreakdown(takeover.creditBreakdown);
              } else if (takeover.offerCount > 1) {
                trackEvent({
                  eventName: VIEWED_TRAVEL_OFFER,
                  properties: {
                    location: "homepage-banner",
                  },
                  encryptedProperties: [
                    takeover.bestOffer?.trackingPropertiesV2
                      ?.encryptedProperties ?? "",
                  ],
                });

                setContent({
                  subtitle: constants.MULTIPLE_OFFER_SUBTITLE(
                    takeover.offerCount
                  ),
                  ctaCopy: constants.VIEW_TRAVEL_OFFERS,
                  ctaAction: () => {
                    history.push({
                      pathname: PATH_TRAVEL_WALLET,
                      hash: "offers",
                    });
                    trackOfferEngagementEvent();
                  },
                  showDismiss: true,
                  className: takeover.Takeover,
                  iconName: "offer-tag",
                });

                trackViewedOfferBannerEvent();
              } else if (
                takeover.bestOffer &&
                !isSpendAndGetOffer(takeover.bestOffer.descriptions)
              ) {
                const offerTakeoverContent = constants.BEST_OFFER_SUBTITLE(
                  takeover.bestOffer
                );
                offerTakeoverContent &&
                  setContent({
                    subtitle: offerTakeoverContent,
                    showTravelOfferModal: true,
                    travelOfferModalContent: getTravelOfferCardContent(
                      takeover.bestOffer,
                      isCreditAndOfferStackingExperimentV1
                    ),
                    className: takeover.Takeover,
                    iconName: "offer-tag",
                    modalVariant: "offer",
                    showDismiss: true,
                  });
                setTakeoverOffer(takeover.bestOffer);

                trackViewedOfferBannerEvent(takeover.bestOffer);
                takeover.bestOffer?.trackingPropertiesV2 &&
                  trackMediaEvent({
                    eventType: MediaEventType.Impression,
                    trackingProperties:
                      takeover.bestOffer?.trackingPropertiesV2,
                    placement: MediaPlacement.HomepageBanner,
                  });
              }
              break;
            default:
              break;
          }
        }
      });
    }
  }, [
    isTravelWalletCreditsExperiment,
    creditsHistoryTransactions,
    isTravelCreditHistoryExperiment,
    cfarDiscountVariant,
    vxbMigrationForceShowFlag,
    vxbMigrationTakeoverFlag,
    rewardsAccounts,
    maintenanceExperiment,
    isMaintenaceBannerActive,
  ]);

  useEffect(() => {
    if (isTravelWalletCreditsExperiment) {
      fetchTravelWalletCreditHistory().then((response) => {
        setCreditsHistoryTransaction(response.credits);
      });
    }
  }, [isTravelCreditHistoryExperiment]);

  if (
    !content ||
    (matchesMobile &&
      ADDITIONAL_MOBILE_REDESIGN_HEADER_FOOTER_PATHS.includes(
        history.location.pathname
      ))
  ) {
    return null;
  }

  return hideTakeover ? null : (
    <>
      <HideOnScroll disable={matchesMobile}>
        <Box
          className={clsx("homepage-takeover-root", {
            mobile: matchesMobile,
            xmod: isXmodRedesign,
          })}
        >
          <Box
            className={clsx("homepage-takeover-container", content.className, {
              mobile: matchesMobile,
            })}
          >
            {matchesMobile ? (
              <>
                {content.iconName ? (
                  <Icon name={content.iconName} className={content.iconName} />
                ) : (
                  <Icon name={IconName.BellWarning} className="bell-icon" />
                )}
                <Box className="content-section">
                  {content.title && (
                    <Typography className="title" variant="body1">
                      {content.title}
                    </Typography>
                  )}
                  {(typeof content.subtitle === "string"
                    ? content.subtitle.trim()
                    : content.subtitle) &&
                    (typeof content.subtitle === "string" ? (
                      <SanitizedHtml
                        as={Typography}
                        className="subtitle"
                        variant="body1"
                        html={content.subtitle}
                      />
                    ) : (
                      <Typography className="subtitle" variant="body1">
                        {content.subtitle}
                      </Typography>
                    ))}
                  {content.ctaAction && content.ctaCopy && (
                    <B2BButton
                      onClick={content.ctaAction}
                      className="homepage-takeover-cta"
                      variant="b2b"
                    >
                      <Typography className="cta-text" variant="body1">
                        {content.ctaCopy}
                      </Typography>
                    </B2BButton>
                  )}
                  {content.showTravelOfferModal && (
                    <TravelWalletDetailsModal
                      openModal={openTravelOfferModal}
                      setOpenModal={(isOpen: boolean) => {
                        if (isOpen) {
                          trackEvent({
                            eventName: ENGAGED_CREDIT_CTA,
                            properties: {},
                          });

                          if (takeoverOffer) {
                            trackOfferEngagementEvent(takeoverOffer);
                          }
                        }
                        setOpenTravelOfferModal(isOpen);
                      }}
                      modalHeaderText={content.modalHeaderText || "Header"}
                      offerCardProps={{
                        offerContent: content.travelOfferModalContent,
                        titleLabelText: takeoverOffer
                          ? isOfferHSPTiered(takeoverOffer?.descriptions)
                          : undefined,
                      }}
                      creditDetailsCardProps={{
                        cardContent: content.creditDetailsCardProps,
                      }}
                      variant={content.modalVariant}
                      modalCTACopy={
                        content.modalVariant === "offer"
                          ? constants.VIEW_OFFER_DETAILS
                          : constants.VIEW_DETAILS
                      }
                      termsType={matchesMobile ? "accordion" : "link"}
                      isMobile={matchesMobile}
                      showCreditHistory={
                        !!(
                          isTravelCreditHistoryExperiment &&
                          content.modalVariant === "credit" &&
                          !!creditHistoryModalContent &&
                          !!creditsHistoryTransactions.length
                        )
                      }
                      travelCreditHistoryProps={
                        isTravelCreditHistoryExperiment &&
                        content.modalVariant === "credit" &&
                        !!creditHistoryModalContent &&
                        !!creditsHistoryTransactions.length
                          ? {
                              modalContentProps: creditHistoryModalContent,
                              viewHistoryCTAText: VIEW_CREDIT_HISTORY_CTA,
                              creditTransactionsHistory:
                                creditsHistoryTransactions,
                              viewCreditHistoryDescription:
                                VIEW_CREDIT_HISTORY_DESCRIPTION,
                              rewardsAccounts,
                              credit: takeoverCredit,
                              creditBreakdown,
                            }
                          : undefined
                      }
                      isAnnualTravelCreditsExperiment={
                        isAnnualTravelCreditsExperiment
                      }
                      offerFunnels={takeoverOffer?.funnels}
                      offerLocation={takeoverOffer?.descriptions[2]}
                      isTravelSale={travelSalesEventVariant !== CONTROL}
                      imageId={takeoverOffer?.imageId}
                      imageFolderUrl={takeoverOffer?.imageFolderUrl}
                      onViewCreditHistory={() =>
                        trackEvent({
                          eventName: VIEW_CREDIT_HISTORY,
                          properties: {},
                        })
                      }
                    />
                  )}
                </Box>
                {content.showDismissMobile ? (
                  <ActionLink
                    className="takeover-dismiss-button"
                    content={<CloseButtonIcon />}
                    label="Close"
                    onClick={() => {
                      setHideTakeover(true);
                      if (content.onDismissAction) {
                        content.onDismissAction();
                      }
                    }}
                  />
                ) : null}
              </>
            ) : (
              <Box className="content-section">
                <div className="dummy-flex-element" />
                <Box className="icon-subtitle-container">
                  {content.iconName ? (
                    <Icon
                      name={content.iconName}
                      className={content.iconName}
                    />
                  ) : (
                    <Icon name={IconName.BellWarning} className="bell-icon" />
                  )}{" "}
                  <Box>
                    {content.title && (
                      <Typography className="title" variant="body1">
                        {content.title}
                      </Typography>
                    )}

                    {(typeof content.subtitle === "string"
                      ? content.subtitle.trim()
                      : content.subtitle) &&
                      (typeof content.subtitle === "string" ? (
                        <SanitizedHtml
                          as={Typography}
                          className="subtitle"
                          variant="body1"
                          html={content.subtitle}
                        />
                      ) : (
                        <Typography className="subtitle" variant="body1">
                          {content.subtitle}
                        </Typography>
                      ))}
                  </Box>
                </Box>
                {content.ctaAction && content.ctaCopy && (
                  <B2BButton
                    onClick={content.ctaAction}
                    className="homepage-takeover-cta"
                    variant="b2b"
                  >
                    <Typography className="cta-text" variant="body1">
                      {content.ctaCopy}
                    </Typography>
                  </B2BButton>
                )}
                {content.showTravelOfferModal && (
                  <TravelWalletDetailsModal
                    openModal={openTravelOfferModal}
                    setOpenModal={(isOpen: boolean) => {
                      if (isOpen) {
                        trackEvent({
                          eventName: ENGAGED_CREDIT_CTA,
                          properties: {},
                        });

                        trackOfferEngagementEvent(takeoverOffer);
                      }
                      setOpenTravelOfferModal(isOpen);
                    }}
                    modalHeaderText={content.modalHeaderText || "Header"}
                    offerCardProps={{
                      offerContent: content.travelOfferModalContent,
                      titleLabelText: takeoverOffer
                        ? isOfferHSPTiered(takeoverOffer?.descriptions)
                        : undefined,
                    }}
                    creditDetailsCardProps={{
                      cardContent: content.creditDetailsCardProps,
                    }}
                    variant={content.modalVariant}
                    modalCTACopy={
                      content.modalVariant === "offer"
                        ? constants.VIEW_OFFER_DETAILS
                        : constants.VIEW_DETAILS
                    }
                    showButton
                    termsType={matchesMobile ? "accordion" : "link"}
                    isMobile={matchesMobile}
                    showCreditHistory={
                      !!(
                        isTravelCreditHistoryExperiment &&
                        content.modalVariant === "credit" &&
                        !!creditHistoryModalContent &&
                        !!creditsHistoryTransactions.length
                      )
                    }
                    travelCreditHistoryProps={
                      isTravelCreditHistoryExperiment &&
                      content.modalVariant === "credit" &&
                      !!creditHistoryModalContent &&
                      !!creditsHistoryTransactions.length
                        ? {
                            modalContentProps: creditHistoryModalContent,
                            viewHistoryCTAText: VIEW_CREDIT_HISTORY_CTA,
                            creditTransactionsHistory:
                              creditsHistoryTransactions,
                            viewCreditHistoryDescription:
                              VIEW_CREDIT_HISTORY_DESCRIPTION,
                            icon: IconName.PiggyBankCircleOutline,
                            rewardsAccounts,
                            credit: takeoverCredit,
                            creditBreakdown,
                          }
                        : undefined
                    }
                    isAnnualTravelCreditsExperiment={
                      isAnnualTravelCreditsExperiment
                    }
                    offerFunnels={takeoverOffer?.funnels}
                    offerLocation={takeoverOffer?.descriptions[2]}
                    isTravelSale={travelSalesEventVariant !== CONTROL}
                    imageId={takeoverOffer?.imageId}
                    imageFolderUrl={takeoverOffer?.imageFolderUrl}
                    onViewCreditHistory={() =>
                      trackEvent({
                        eventName: VIEW_CREDIT_HISTORY,
                        properties: {},
                      })
                    }
                  />
                )}
                {content.showDismiss ? (
                  <ActionLink
                    className="takeover-dismiss-button"
                    content={<CloseButtonIcon />}
                    label="Close"
                    onClick={() => {
                      setHideTakeover(true);
                      if (content.onDismissAction) {
                        content.onDismissAction();
                      }
                    }}
                  />
                ) : null}
              </Box>
            )}
          </Box>
        </Box>
      </HideOnScroll>
      <GenericDetailsCard
        popupClassName="refundable-fare-generic-details-card"
        openModal={openRFModal}
        onClose={() => setOpenRFModal(false)}
        topBannerContent={{
          iconName: IconName.CheckShieldBlue,
          bannerText: `${discountPromoText.modalBanner}`,
          component: GenericDetailsCardComponentEnum.TopBanner,
        }}
        topContent={{
          image: CDN_ASSETS.capone.cfar.backgroundDesktop,
          location: REFUNDABLE_FARES_LOCATION,
          header: REFUNDABLE_FARES_TITLE,
          component:
            GenericDetailsCardTopSectionComponentEnum.HeaderWithImageBackground,
        }}
        mainContent={[
          {
            id: "cfar-dicount",
            content: discountContent,
            component: GenericDetailsCardComponentEnum.Custom,
          },
          {
            type: "secondary",
            component: GenericDetailsCardComponentEnum.GenericCopy,
            copy: REFUNDABLE_FARES_POINT_ONE({
              variant: "v1",
              useStrong: true,
              punctuation: ".",
            }),
            icon: IconName.NotAllowedSignBlue,
          },
          {
            type: "secondary",
            component: GenericDetailsCardComponentEnum.GenericCopy,
            copy: REFUND_COVERAGE_PERCENTAGE_HARDCODE_80,
            icon: IconName.MoneyCounterClockwiseBlue,
          },
          {
            type: "secondary",
            component: GenericDetailsCardComponentEnum.GenericCopy,
            copy: REFUNDABLE_FARES_POINT_THREE(getPortalName(), {
              punctuation: ".",
            }),
            icon: IconName.SimpleLaptopBlue,
          },
          {
            message: TERMS_COPY,
            component: GenericDetailsCardComponentEnum.ClickableLink,
            onClick: () => window.open(PATH_TERMS_CFAR, "_blank")?.focus(),
            position: "center",
          },
          {
            type: "secondary",
            component: GenericDetailsCardComponentEnum.GenericCopy,
            copy: DISCOUNT_PROMO_TERMS_CONDITIONS(getPortalName()),
          },
        ]}
        contentOnly={false}
        contentClassName="refundable-fare-details-modal"
      />
    </>
  );
};

const fetchLandingPageTakeovers = async () => {
  try {
    const takeovers = await landingPageTakeovers();
    return takeovers;
  } catch (err) {
    console.log(err);
    return [];
  }
};
